import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, formatCurrency } from '../../utils/api'
import ConditionPicker, { TESTING_CONDITION_OPTIONS, gridColumnsForBreakpoint } from './ConditionPicker'
import useBreakpoint from '../../hooks/useBreakpoint.jsx'
import { displayCondition } from './conditionDisplay'
import { applyCategoryPick, isCategoryReadyForTesting, dealLinkProps, isElectricalChecklist, testingLevelsFor } from './testingHelpers'
import UntestedConfirmModal from './UntestedConfirmModal'
import ClickyTextNotes from './ClickyTextNotes'
import { useAuth } from '../../hooks/useAuth'
import { itemQuantity, itemCountPhrase } from '../../../../shared/item-count.js'
import QuantityBadge from '../shared/QuantityBadge.jsx'
import CustomerNotePanel from '../deals/quote-assessment/CustomerNotePanel.jsx'
import CustomerDealsPanel from './CustomerDealsPanel'
import useCustomerSubmission from '../deals/quote-assessment/useCustomerSubmission.js'
import QuoteNotesLayout from './QuoteNotes'
import DealLink from '../shared/DealLink.jsx'
import ProductTypeSelector, { getMainCategoriesFromGroups } from './ProductTypeSelector'
import { SellAsPanel, PartTabs } from './SplitPanel'
import IncludedItemsPicker from './IncludedItemsPicker.jsx'
import { toggleIncludedItem as toggleIncludedSelection, includedItemsAnswered, includedItemsForSubmit } from './includedItems.js'
import {
  lineBuyPence, startSplit, addPart, removePart, updatePart, splitProblems, wholeItemRecord, splitDoneLabel
} from './splitItem'

// Design System
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Constants
const PHYSICAL_SCORE_OPTIONS = [
  { value: 'Fair',      label: 'Fair' },
  { value: 'Good',      label: 'Good' },
  { value: 'Very Good', label: 'Very Good' },
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Mint',      label: 'Like New' },
]
const CHECKLIST_RESULTS = ['OK', 'FAIL', 'N/A']

const PHYSICAL_FIELDS = [
  { key: 'scratching', label: 'Scratching & Scraping' },
  { key: 'dings', label: 'Dings and Dents' },
  { key: 'colouration', label: 'Colouration' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'integrity', label: 'Physical Integrity' }
]

// Components
const Card = ({ children, style = {}, padding = true }) => {
  const breakpoint = useBreakpoint()
  const pad = padding ? (breakpoint === 'phone' ? '16px' : '24px') : 0
  return (
    <div style={{
      background: theme.colors.white,
      borderRadius: theme.radius.lg,
      boxShadow: theme.shadows.sm,
      border: `1px solid ${theme.colors.grey200}`,
      padding: pad,
      ...style
    }}>
      {children}
    </div>
  )
}

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick, style = {} }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    warning: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s, ...style,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20, light = false }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${light ? 'rgba(255,255,255,0.3)' : theme.colors.grey200}`,
    borderTopColor: light ? 'white' : theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

const Alert = ({ type = 'info', children }) => {
  const types = {
    info: { bg: theme.colors.blueLight, border: theme.colors.imBlue, icon: 'ℹ️' },
    warning: { bg: theme.colors.amberLight, border: theme.colors.sunburst, icon: '⚠️' },
    error: { bg: theme.colors.redLight, border: theme.colors.crimson, icon: '✕' },
    success: { bg: theme.colors.greenLight, border: theme.colors.greenOnions, icon: '✓' }
  }
  const t = types[type]
  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: theme.radius.md,
      padding: '12px 16px',
      fontSize: '14px',
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    }}>
      <span>{t.icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

// Option Button Group
const OptionGroup = ({ options, value, onChange, columns = 5 }) => {
  const breakpoint = useBreakpoint()
  const cols = gridColumnsForBreakpoint({ breakpoint, optionCount: options.length, explicit: columns })
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '8px' }}>
    {options.map(opt => {
      const optValue = opt.value || opt
      const optLabel = opt.label || opt
      return (
        <button
          key={optValue}
          onClick={() => onChange(optValue)}
          style={{
            padding: '10px 8px',
            border: `2px solid ${value === optValue ? theme.colors.greenOnions : theme.colors.grey200}`,
            borderRadius: theme.radius.md,
            background: value === optValue ? theme.colors.greenLight : theme.colors.white,
            fontWeight: value === optValue ? 600 : 400,
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
        >
          {optLabel}
        </button>
      )
    })}
    </div>
  )
}

// Item Tabs Component for multi-item deals
function ItemTabs({ items, currentIndex, completedIndices, inProgressIndices, onSelectItem }) {
  const containerRef = React.useRef(null)

  // An item still in `pending` state was never received at goods-in, so it
  // cannot be tested (Prism refuses pending -> tested_ok). Show it, but locked.
  const isNotReceived = (index) => items[index]?.state === 'pending'
  const testableTotal = items.filter(it => it?.state !== 'pending').length

  const getItemStatus = (index) => {
    if (isNotReceived(index)) return 'not_received'
    if (completedIndices.includes(index)) return 'completed'
    if (index === currentIndex) return 'current'
    if (inProgressIndices.includes(index)) return 'in_progress'
    return 'pending'
  }

  const handleTabClick = (index) => {
    if (index === currentIndex) return
    if (isNotReceived(index)) return
    onSelectItem(index)
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          gap: '8px',
          overflowX: items.length > 5 ? 'auto' : 'visible',
          paddingBottom: items.length > 5 ? '8px' : 0,
          scrollbarWidth: 'thin'
        }}
      >
        {items.map((item, index) => {
          const status = getItemStatus(index)
          const shortName = (item.name || item.productName || `Item ${index + 1}`).substring(0, 25)
          const displayName = shortName.length < (item.name || item.productName || '').length
            ? shortName + '...'
            : shortName

          const notReceived = status === 'not_received'

          return (
            <button
              key={index}
              onClick={() => handleTabClick(index)}
              disabled={notReceived}
              title={notReceived ? 'Not received at goods-in — receive it before testing' : undefined}
              style={{
                flex: items.length <= 5 ? 1 : '0 0 auto',
                minWidth: items.length > 5 ? '140px' : 'auto',
                padding: '12px 16px',
                border: status === 'current'
                  ? `2px solid ${theme.colors.sunburst}`
                  : status === 'in_progress'
                    ? `1px solid ${theme.colors.sunburst}`
                    : notReceived
                      ? `1px dashed ${theme.colors.grey300}`
                      : `1px solid ${theme.colors.grey200}`,
                borderRadius: theme.radius.md,
                background: status === 'completed'
                  ? theme.colors.greenLight
                  : status === 'in_progress'
                    ? theme.colors.amberLight
                    : status === 'current'
                      ? theme.colors.white
                      : theme.colors.grey100,
                opacity: notReceived ? 0.55 : 1,
                cursor: notReceived ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.15s ease',
                position: 'relative'
              }}
            >
              {/* Status indicator */}
              <div style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: status === 'completed'
                  ? theme.colors.greenOnions
                  : status === 'current'
                    ? theme.colors.sunburst
                    : status === 'in_progress'
                      ? theme.colors.sunburst
                      : theme.colors.grey300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: status === 'completed' ? 'white' : theme.colors.charcoal,
                fontWeight: 600
              }}>
                {status === 'completed' ? '✓' : notReceived ? '🔒' : index + 1}
              </div>

              {/* Item number (or a not-received marker) */}
              <span style={{
                fontSize: '11px',
                color: notReceived ? theme.colors.crimson : theme.colors.grey500,
                fontWeight: notReceived ? 600 : 500
              }}>
                {notReceived ? 'Not received' : `Item ${index + 1}`}
              </span>

              {/* A line of several items: each one is tested (spec 20261008). */}
              <QuantityBadge quantity={item.quantity} />

              {/* Item name */}
              <span style={{
                fontSize: '12px',
                fontWeight: status === 'current' ? 600 : 400,
                color: theme.colors.charcoal,
                textAlign: 'center',
                lineHeight: 1.2,
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {displayName}
              </span>
            </button>
          )
        })}
      </div>

      {/* Progress indicator */}
      <div style={{
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: theme.colors.grey500
      }}>
        <div style={{
          flex: 1,
          height: '4px',
          background: theme.colors.grey200,
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${(completedIndices.length / Math.max(testableTotal, 1)) * 100}%`,
            background: theme.colors.greenOnions,
            borderRadius: '2px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span>{completedIndices.length} of {testableTotal} completed</span>
      </div>
    </div>
  )
}

// Build the per-item payload (same shape the bundle save-results endpoint expects).
function buildItemResult(testResult, item, itemIndex) {
  // A split item's own record is built from its parts (Trac #1111).
  const whole = testResult.split ? wholeItemRecord(testResult.split.parts) : null
  const record = whole ? { ...testResult, ...whole } : testResult
  testResult = record
  return {
    split: record.split || undefined,
    itemId: item?.id,
    itemName: item?.name || item?.productName,
    itemIndex,
    overallCondition: testResult.overallCondition,
    brokenDetails: testResult.brokenDetails,
    sparesAmount: testResult.sparesAmount ? parseFloat(testResult.sparesAmount) : null,
    physicalCondition: testResult.physicalCondition,
    testingLevel: testResult.testingLevel,
    untestedReason: testResult.untestedReason || null,
    checklistResults: testResult.checklistResults,
    includedItems: includedItemsForSubmit(testResult.includedItems),
    otherAccessories: testResult.otherAccessories,
    notes: testResult.notes,
    needsRepair: testResult.needsRepair,
    repairIssues: testResult.repairIssues
  }
}

// Has the user filled in anything worth persisting? Used to skip empty save-draft calls.
function testResultHasContent(t) {
  if (t.split) return true
  if (t.overallCondition) return true
  if (t.brokenDetails) return true
  if (t.sparesAmount) return true
  if (t.testingLevel) return true
  if (t.notes) return true
  if (t.includedItems.length > 0) return true
  if (t.needsRepair) return true
  if (Object.values(t.physicalCondition).some(v => v !== null)) return true
  if (Object.keys(t.checklistResults).length > 0) return true
  return false
}

// Replace-or-append by itemIndex.
function upsertItemResult(list, entry) {
  const idx = list.findIndex(r => r.itemIndex === entry.itemIndex)
  if (idx >= 0) {
    const next = [...list]
    next[idx] = entry
    return next
  }
  return [...list, entry]
}

function Testing() {
  const [searchParams, setSearchParams] = useSearchParams()
  const bp = useBreakpoint()
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [deal, setDeal] = useState(null)
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [step, setStep] = useState('search') // search, results, testing, success
  const [productTypes, setProductTypes] = useState(null)
  const [productTypesLoading, setProductTypesLoading] = useState(false)
  const [showProductTypeSelector, setShowProductTypeSelector] = useState(false)
  const [checklist, setChecklist] = useState(null)
  const [includedItemsOptions, setIncludedItemsOptions] = useState([])
  const [saving, setSaving] = useState(false)

  // Store all item results locally until final submission
  const [allItemResults, setAllItemResults] = useState([])
  const [submitResult, setSubmitResult] = useState(null) // API response after final submit
  // Manual photography-queue state removed — auto-queued at save-results time (Trac #867).

  // Per-item test results (current item being tested)
  const [testResult, setTestResult] = useState({
    overallCondition: null,
    brokenDetails: '',
    sparesAmount: '',
    physicalCondition: {
      scratching: null,
      dings: null,
      colouration: null,
      cleanliness: null,
      integrity: null
    },
    testingLevel: null,
    untestedReason: '',
    checklistResults: {},
    includedItems: [],
    otherAccessories: '', // Free text for "Other" accessories
    notes: '',
    needsRepair: false,
    repairIssues: '',
    split: null
  })
  // Which part of a split item the form below is editing.
  const [activePartIndex, setActivePartIndex] = useState(0)

  const { user } = useAuth()
  const [showUntestedModal, setShowUntestedModal] = useState(false)

  // The customer's note on their trade-in, read once per deal (no-op until one is picked).
  const submission = useCustomerSubmission(deal?.id)

  const currentItem = deal?.items?.[currentItemIndex]
  // Split (Trac #1111): the whole line's buy price is what the parts' cost shares add up to.
  const lineBuy = currentItem ? lineBuyPence(currentItem) : 0
  const activePart = testResult.split?.parts[activePartIndex] ?? null
  // The fields the test cards edit: the active part's own, or the whole item's.
  const view = activePart ?? testResult
  const hasCondition = !!(testResult.split || testResult.overallCondition)
  const isElectrical = isElectricalChecklist(checklist)
  const testingLevelOptions = testingLevelsFor(checklist)

  // Per-item draft status drives the tab UI: complete = green tick, in_progress = amber dot.
  const completedItemIndices = allItemResults.filter(r => r.status === 'complete').map(r => r.itemIndex)
  const inProgressItemIndices = allItemResults.filter(r => r.status === 'in_progress').map(r => r.itemIndex)

  // Only received items are testable — a `pending` item was never taken in at
  // goods-in and Prism refuses to test it (pending -> tested_ok). These drive the
  // submit gate and item navigation so the operator can finish the received
  // subset without the un-received items ever blocking completion.
  const testableIndices = (deal?.items || [])
    .map((item, idx) => (item?.state === 'pending' ? -1 : idx))
    .filter(idx => idx >= 0)
  const testableCount = testableIndices.length

  // Guards a single bundle-submit when the last item flips to complete (Trac #850 Phase 1).
  const submittedRef = useRef(false)
  // Mirror of allItemResults for use inside debounced timers without re-running the effect.
  const allItemResultsRef = useRef(allItemResults)
  allItemResultsRef.current = allItemResults
  // The pending autosave timer and the save in flight, so Cancel can stop one and wait for the other.
  const draftTimerRef = useRef(null)
  const draftSaveRef = useRef(Promise.resolve())
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  // A deal the search found but cannot test: the screen says why and shows
  // the same customer's other deals.
  const [notActionable, setNotActionable] = useState(null)

  // Restores form state from a draft / completed item result.
  const applyResultToTestState = (result) => {
    setTestResult({
      overallCondition: result.overallCondition || null,
      brokenDetails: result.brokenDetails || '',
      sparesAmount: result.sparesAmount?.toString() || '',
      physicalCondition: result.physicalCondition || {
        scratching: null, dings: null, colouration: null, cleanliness: null, integrity: null
      },
      testingLevel: result.testingLevel || null,
      untestedReason: result.untestedReason || '',
      checklistResults: result.checklistResults || {},
      includedItems: result.includedItems || [],
      otherAccessories: result.otherAccessories || '',
      notes: result.notes || '',
      needsRepair: result.needsRepair || false,
      repairIssues: result.repairIssues || '',
      split: result.split || null
    })
    setActivePartIndex(0)
  }

  // Save the current item's state now, before the auto-save effect tears down its
  // debounce timer. Fire-and-forget; the user is moving on. Preserve 'complete'
  // status if the item is still valid; otherwise drop to 'in_progress'.
  const flushCurrentDraft = () => {
    if (!deal || !currentItem || !testResultHasContent(testResult)) return
    const itemResult = buildItemResult(testResult, currentItem, currentItemIndex)
    const existing = allItemResults.find(r => r.itemIndex === currentItemIndex)
    const status = (existing?.status === 'complete' && canSubmit()) ? 'complete' : 'in_progress'
    api.post('/api/warehouse/testing/save-draft', {
      dealId: deal.id,
      itemIndex: currentItemIndex,
      status,
      result: itemResult
    }).catch(err => console.error('Failed to flush draft:', err))
    setAllItemResults(prev => upsertItemResult(prev, { ...itemResult, itemIndex: currentItemIndex, status }))
  }

  // Switch to a different item
  const switchToItem = async (index) => {
    if (index === currentItemIndex) return
    // Un-received items are locked — never make one the active item.
    if (deal?.items?.[index]?.state === 'pending') return

    flushCurrentDraft()

    setCurrentItemIndex(index)
    setShowProductTypeSelector(false)

    // Check if this item was already completed/in-progress - restore its data
    const existingResult = allItemResults.find(r => r.itemIndex === index)
    if (existingResult) {
      applyResultToTestState(existingResult)
    } else {
      resetTestResult()
    }

    // Load checklist for new item
    const newItem = deal?.items?.[index]
    if (newItem?.productType) {
      await loadChecklist(newItem.productType)
    } else {
      setChecklist(null)
      setIncludedItemsOptions([])
    }
  }

  // Broken item detection
  // Customer-stated condition arrives normalised from the backend
  // (warehouse-system CONDITION_NORMALIZE): "not working" / "non-functioning"
  // / "broken" variants are all mapped to 'Broken'. Must compare against the
  // post-normalisation value, not the pre-normalisation 'Not Working'.
  const isBroken = testResult.overallCondition === 'Broken'
  const customerExpectedBroken = currentItem?.customerCondition === 'Broken'
  const isUnexpectedlyBroken = isBroken && !customerExpectedBroken

  // Auto-load deal from URL query parameter
  useEffect(() => {
    const dealId = searchParams.get('dealId')
    if (dealId && step === 'search' && !deal) {
      setSearchQuery(dealId)
      loadDealById(dealId)
      setSearchParams({})
    }
  }, [searchParams])

  const resetTestResult = () => {
    setTestResult({
      overallCondition: null,
      brokenDetails: '',
      sparesAmount: '',
      physicalCondition: {
        scratching: null,
        dings: null,
        colouration: null,
        cleanliness: null,
        integrity: null
      },
      testingLevel: null,
      checklistResults: {},
      includedItems: [],
      otherAccessories: '',
      notes: '',
      needsRepair: false,
      repairIssues: '',
      split: null
    })
    setActivePartIndex(0)
  }

  // An empty search: say why the found deal cannot be tested, or that nothing matched.
  const showNoTestableDeal = (response, query) => {
    if (response.notActionable) {
      setNotActionable(response.notActionable)
      setError(response.notActionable.reason)
    } else {
      setError(`No deal matches "${String(query).trim()}". There is no customer to look up.`)
    }
  }

  const loadDealById = async (dealId) => {
    setLoading(true)
    setError(null)
    setNotActionable(null)
    try {
      const response = await api.get(`/api/warehouse/testing/search?q=${encodeURIComponent(dealId)}`)
      const deals = response.deals || []
      if (deals.length === 0) {
        showNoTestableDeal(response, dealId)
      } else {
        selectDeal(deals[0])
      }
    } catch (err) {
      setError(err.message || 'Failed to load deal')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setLoading(true)
    setError(null)
    setNotActionable(null)
    setSearchResults([])
    try {
      const response = await api.get(`/api/warehouse/testing/search?q=${encodeURIComponent(searchQuery)}`)
      const deals = response.deals || []
      if (deals.length === 0) {
        showNoTestableDeal(response, searchQuery)
      } else if (deals.length === 1) {
        selectDeal(deals[0])
      } else {
        setSearchResults(deals)
        setStep('results')
      }
    } catch (err) {
      setError(err.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  // Test on a panel row: look the deal up afresh, so a row that went stale cannot be tested.
  // Leaving the deal on screen keeps its drafts, as New Search does.
  const openFromPanel = async (row) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/warehouse/testing/search?q=${encodeURIComponent(row.id)}`)
      const [found] = response.deals || []
      if (found) {
        // Keep the edit made just before the click, then clear the old deal's
        // results, so none can be submitted against the new one.
        flushCurrentDraft()
        reset()
        setSearchQuery(row.reference)
        selectDeal(found)
      } else {
        setError(response.notActionable?.reason || `${row.reference} can no longer be tested.`)
      }
    } catch (err) {
      setError(err.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const selectDeal = async (selectedDeal) => {
    setDeal(selectedDeal)
    // Land on the first testable (received) item — item 0 may be un-received and
    // its tab is locked, so starting there would strand the operator.
    const firstTestable = (selectedDeal.items || []).findIndex(it => it?.state !== 'pending')
    const startIndex = firstTestable >= 0 ? firstTestable : 0
    setCurrentItemIndex(startIndex)
    resetTestResult()
    setSubmitResult(null)
    submittedRef.current = false
    setStep('testing')

    // Restore any drafts for this deal (Trac #850 Phase 1).
    let drafts = []
    try {
      const response = await api.get(`/api/warehouse/testing/drafts?dealId=${encodeURIComponent(selectedDeal.id)}`)
      drafts = response.drafts || []
    } catch (err) {
      console.error('Failed to load drafts:', err)
    }
    setAllItemResults(drafts.map(d => ({
      ...d.result,
      itemIndex: d.itemIndex,
      status: d.status
    })))

    // If the starting item has a draft, restore the form state so the user picks
    // up where they left off.
    const firstDraft = drafts.find(d => d.itemIndex === startIndex)
    if (firstDraft) {
      applyResultToTestState(firstDraft.result)
    }

    if (selectedDeal.items?.[startIndex]?.productType) {
      await loadChecklist(selectedDeal.items[startIndex].productType)
    }
  }

  const loadChecklist = async (productType) => {
    if (!productType) {
      setChecklist(null)
      setIncludedItemsOptions([])
      return
    }
    try {
      const response = await api.get(`/api/warehouse/testing/checklist?productType=${encodeURIComponent(productType)}`)
      setChecklist(response)
      setIncludedItemsOptions(response.includedItems || [])
    } catch (err) {
      setChecklist(null)
      setIncludedItemsOptions([])
    }
  }

  const loadProductTypes = async () => {
    if (productTypes) return
    setProductTypesLoading(true)
    try {
      const response = await api.get('/api/warehouse/testing/product-types')
      setProductTypes(response)
    } catch (err) {
      console.error('Failed to load product types:', err)
    } finally {
      setProductTypesLoading(false)
    }
  }

  const openProductTypeSelector = async () => {
    await loadProductTypes()
    setShowProductTypeSelector(true)
  }

  const handleProductTypeSelect = async (productType, bucket) => {
    // applyCategoryPick synchronises all four productType* fields. Keeping
    // the spread inside a tested helper guards against the partial-state
    // bug that originally landed (productType updated but needsReview
    // stuck true — testingHelpers.test.js has a regression test for it).
    setDeal(prev => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === currentItemIndex ? applyCategoryPick(item, productType, bucket) : item
      )
    }))

    // Persist in two places: the name-keyed `products` catalog (future deals
    // with this product_name benefit), AND deal_item_categories for THIS
    // specific deal/item (so a reload doesn't re-canonicalise the original
    // legacy value and re-flag for review). Both writes are best-effort —
    // failures get logged but the React state has already cleared the gate.
    try {
      const productName = currentItem?.productName || currentItem?.name
      if (productName) {
        await api.post('/api/warehouse/testing/set-product-type', {
          productName,
          productType
        })
      }
    } catch (err) {
      console.error('Failed to persist product type to catalog:', err)
    }

    try {
      // itemIndex is 0-based to match what enrichItemsWithProductTypes looks up.
      // currentItem.index is 1-based from parseItems; -1 converts.
      const itemIndex = ((currentItem?.index ?? (currentItemIndex + 1)) - 1)
      if (deal?.id) {
        await api.post('/api/warehouse/testing/set-deal-category', {
          dealId: deal.id,
          itemIndex,
          productType
        })
      }
    } catch (err) {
      console.error('Failed to persist deal category:', err)
    }

    await loadChecklist(productType)
    setShowProductTypeSelector(false)
  }

  // Edit the fields the test cards show: `change` gets the active part (or the
  // whole item) and returns it changed.
  const updateView = (change) => {
    setTestResult(prev => prev.split
      ? { ...prev, split: { ...prev.split, parts: prev.split.parts.map((part, i) => (i === activePartIndex ? change(part) : part)) } }
      : change(prev))
  }

  const updatePhysicalCondition = (field, value) => {
    updateView(v => ({ ...v, physicalCondition: { ...v.physicalCondition, [field]: value } }))
  }

  const updateChecklistResult = (key, value) => {
    updateView(v => ({ ...v, checklistResults: { ...v.checklistResults, [key]: value } }))
  }

  const toggleIncludedItem = (item) => {
    updateView(v => ({
      ...v,
      includedItems: toggleIncludedSelection(v.includedItems, item)
    }))
  }

  // Sell as: One listing or Split into separate listings (Trac #1111).
  const startSplitting = () => {
    setTestResult(prev => ({ ...prev, split: startSplit(lineBuy) }))
    setActivePartIndex(0)
    setChecklist(null)
    setIncludedItemsOptions([])
  }

  const stopSplitting = () => {
    setTestResult(prev => ({ ...prev, split: null }))
    setActivePartIndex(0)
    loadChecklist(currentItem?.productType)
  }

  const selectPart = (index) => {
    setActivePartIndex(index)
    loadChecklist(testResult.split.parts[index].subCategory)
  }

  const addSplitPart = () => {
    setTestResult(prev => ({ ...prev, split: addPart(prev.split, lineBuy) }))
  }

  const removeSplitPart = (index) => {
    const next = removePart(testResult.split, index, lineBuy)
    setTestResult(prev => ({ ...prev, split: next }))
    setActivePartIndex(0)
    loadChecklist(next.parts[0].subCategory)
  }

  const changeSplitPart = (index, patch) => {
    // A new category means a different checklist, so the old answers go.
    const fullPatch = 'subCategory' in patch
      ? { ...patch, checklistResults: {}, includedItems: [], otherAccessories: '' }
      : patch
    setTestResult(prev => ({ ...prev, split: updatePart(prev.split, index, fullPatch, lineBuy) }))
    if ('subCategory' in patch && index === activePartIndex) loadChecklist(patch.subCategory)
  }

  // Set by warehouse-system enrichment when the LLM emitted a non-canonical
  // SUB_CAT that the legacy→canonical mapping couldn't resolve. We show the
  // raw suggestion and hard-block save until the tech picks a canonical type.
  const needsCategoryReview = !!currentItem?.productTypeNeedsReview
  const suggestedCategory = currentItem?.productTypeSuggested

  // The split option is for a plain passed item: never consignment, never
  // spares or repair, never a repair-list item (the worker refuses all three).
  const splitAllowed = !deal?.isListingService && !testResult.needsRepair && testResult.overallCondition !== 'Broken'
  const splitIssues = testResult.split ? splitProblems(testResult.split, lineBuy) : []
  const testingLevelForRecord = testResult.split
    ? wholeItemRecord(testResult.split.parts).testingLevel
    : testResult.testingLevel

  // W7: the included-items step is required whenever it shows.
  const needsIncludedItems = includedItemsOptions.length > 0 && hasCondition && !isUnexpectedlyBroken

  const canSubmit = () => {
    // Category gate is the pure helper used by the form-hide check below too,
    // so the boolean stays consistent across surfaces (testingHelpers.test.js).
    if (!isCategoryReadyForTesting(currentItem)) return false
    if (testResult.split) return splitIssues.length === 0
    if (!testResult.overallCondition) return false

    // Every Broken result needs a spares amount, expected or not: for a Broken
    // item it IS Prism's committed payout, and without it the item has no
    // payable figure (blocks the payout and every later testing email).
    if (isBroken && !testResult.sparesAmount) return false

    if (needsIncludedItems && !includedItemsAnswered(testResult.includedItems, testResult.otherAccessories)) return false

    if (isUnexpectedlyBroken) {
      if (!testResult.brokenDetails) return false
    } else if (!isBroken) {
      // Physical condition required for non-broken
      const physicalComplete = Object.values(testResult.physicalCondition).every(v => v !== null)
      if (!physicalComplete) return false
      if (!testResult.testingLevel) return false
    }

    return true
  }

  // Debounced auto-save (Trac #850 Phase 1). Persists the current item's draft 500ms
  // after the last edit so crashes / refreshes don't lose work. Auto-save NEVER flips
  // an item to 'complete' on its own — only the explicit "Mark item complete" button
  // does that. If the user re-edits a previously-completed item and breaks a required
  // field, status drops back to 'in_progress' so they re-confirm.
  useEffect(() => {
    if (!deal || !currentItem || step !== 'testing') return
    if (!testResultHasContent(testResult)) return

    const timer = setTimeout(async () => {
      const itemResult = buildItemResult(testResult, currentItem, currentItemIndex)
      const existing = allItemResultsRef.current.find(r => r.itemIndex === currentItemIndex)
      const status = (existing?.status === 'complete' && canSubmit()) ? 'complete' : 'in_progress'
      try {
        draftSaveRef.current = api.post('/api/warehouse/testing/save-draft', {
          dealId: deal.id,
          itemIndex: currentItemIndex,
          status,
          result: itemResult
        })
        await draftSaveRef.current
        setAllItemResults(prev => upsertItemResult(prev, { ...itemResult, itemIndex: currentItemIndex, status }))
      } catch (err) {
        console.error('Failed to save draft:', err)
      }
    }, 500)
    draftTimerRef.current = timer

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testResult, currentItemIndex, deal?.id, step, currentItem?.productType])

  // Bundle-submit effect (Trac #850 Phase 1). Fires the existing save-results pipeline
  // exactly once when every item flips to 'complete'. Backend deletes drafts on success.
  useEffect(() => {
    if (!deal || step !== 'testing') return
    if (submittedRef.current) return

    // Submit fires when every TESTABLE item is complete — un-received items are
    // excluded (they can't be tested), so they must not hold the save open, and
    // a stale complete-draft on one must not be sent for recording.
    const completeResults = allItemResults.filter(
      r => r.status === 'complete' && testableIndices.includes(r.itemIndex)
    )
    if (testableCount === 0 || completeResults.length !== testableCount) return

    submittedRef.current = true
    setSaving(true)
    setError(null)

    api.post('/api/warehouse/testing/save-results', {
      dealId: deal.id,
      itemResults: completeResults,
      items: deal.items,
      isListingService: deal.isListingService || false
    })
      .then(response => {
        setSubmitResult(response)
        setStep('success')
      })
      .catch(err => {
        submittedRef.current = false
        setError(err.message || 'Failed to submit deal')
      })
      .finally(() => {
        setSaving(false)
      })
  }, [allItemResults, deal, step])

  // Explicit user action — flips the current item to 'complete' and auto-advances to the
  // next incomplete item (matches the previous Save & Continue behavior). Bundle-submit
  // useEffect fires on its own when this is the last item.
  const markCurrentItemComplete = async () => {
    if (!deal || !currentItem || !canSubmit() || saving) return
    // Untested items require an explicit confirmation + justification.
    if (testingLevelForRecord === 'Untested') {
      setShowUntestedModal(true)
      return
    }
    return doMarkComplete()
  }

  const doMarkComplete = async (reasonOverride) => {
    if (!deal || !currentItem || saving) return
    setSaving(true)
    setError(null)
    try {
      const effectiveTestResult = reasonOverride
        ? { ...testResult, untestedReason: reasonOverride }
        : testResult
      const itemResult = buildItemResult(effectiveTestResult, currentItem, currentItemIndex)
      await api.post('/api/warehouse/testing/save-draft', {
        dealId: deal.id,
        itemIndex: currentItemIndex,
        status: 'complete',
        result: itemResult,
        userName: user?.name || null
      })
      const updated = upsertItemResult(allItemResults, { ...itemResult, itemIndex: currentItemIndex, status: 'complete' })
      setAllItemResults(updated)

      // Auto-advance to next incomplete item. Only testable (received) items
      // count toward completion and are eligible to advance to — un-received
      // items are locked and never become complete.
      const completeIndices = updated.filter(r => r.status === 'complete').map(r => r.itemIndex)
      const allComplete = testableIndices.every(idx => completeIndices.includes(idx))
      if (!allComplete) {
        let nextIndex = -1
        for (let i = currentItemIndex + 1; i < deal.items.length; i++) {
          if (testableIndices.includes(i) && !completeIndices.includes(i)) { nextIndex = i; break }
        }
        if (nextIndex === -1) {
          for (let i = 0; i < currentItemIndex; i++) {
            if (testableIndices.includes(i) && !completeIndices.includes(i)) { nextIndex = i; break }
          }
        }
        if (nextIndex >= 0) {
          setCurrentItemIndex(nextIndex)
          setShowProductTypeSelector(false)
          const existingNext = updated.find(r => r.itemIndex === nextIndex)
          if (existingNext) {
            applyResultToTestState(existingNext)
          } else {
            resetTestResult()
          }
          const nextItem = deal.items[nextIndex]
          if (nextItem?.productType) {
            await loadChecklist(nextItem.productType)
          } else {
            setChecklist(null)
            setIncludedItemsOptions([])
          }
        }
      }
      // If allComplete, the bundle-submit useEffect picks up updated allItemResults
      // and fires save-results.
    } catch (err) {
      setError(err.message || 'Failed to mark item complete')
    } finally {
      setSaving(false)
    }
  }

  // Operative confirmed an untested item with a justification (>= 20 chars).
  const confirmUntested = async (reason) => {
    setShowUntestedModal(false)
    setTestResult(prev => ({ ...prev, untestedReason: reason }))
    await doMarkComplete(reason)
  }

  const reset = () => {
    setSearchQuery('')
    setSearchResults([])
    setDeal(null)
    setCurrentItemIndex(0)
    setStep('search')
    setChecklist(null)
    setIncludedItemsOptions([])
    resetTestResult()
    setError(null)
    setShowProductTypeSelector(false)
    setAllItemResults([])
    setSubmitResult(null)
    setConfirmingCancel(false)
    setNotActionable(null)
    submittedRef.current = false
  }

  // Cancel: throw away this item's draft and go back to the search. Stop the
  // pending autosave and wait for one in flight, or it would write the draft
  // back after the delete.
  const discardCurrentItem = async () => {
    clearTimeout(draftTimerRef.current)
    setSaving(true)
    setError(null)
    try {
      await draftSaveRef.current.catch(() => {})
      await api.post('/api/warehouse/testing/discard-draft', { dealId: deal.id, itemIndex: currentItemIndex })
      reset()
    } catch (err) {
      setConfirmingCancel(false)
      setError(err.message || 'Could not discard this item')
    } finally {
      setSaving(false)
    }
  }

  // Format accessory ID to display name (e.g., "original_box" -> "Original Box")
  const formatAccessoryName = (id) => {
    if (!id) return id
    return id
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
  }


  // Search Screen
  if (step === 'search') {
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ padding: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🔧</div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Product Testing</h2>
            <p style={{ color: theme.colors.grey500 }}>Enter a Deal ID to begin testing</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              placeholder="Enter Deal ID"
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px',
                fontFamily: theme.fonts.sans,
                textAlign: 'center', outline: 'none'
              }}
            />
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <Button onClick={handleSearch} disabled={loading || !searchQuery.trim()} fullWidth size="lg">
            {loading ? <><Spinner size={18} /> Searching...</> : 'Search'}
          </Button>
        </Card>
        {notActionable && (
          <div style={{ marginTop: '16px' }}>
            <CustomerDealsPanel dealId={notActionable.dealId} screen="testing" onAct={openFromPanel} />
          </div>
        )}
      </div>
    )
  }

  // Results Screen
  if (step === 'results') {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
            {searchResults.length} Deal{searchResults.length !== 1 ? 's' : ''} Found
          </h2>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {searchResults.map(d => (
          <Card key={d.id} onClick={() => selectDeal(d)} style={{ marginBottom: '12px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                  <DealLink {...dealLinkProps(d)} newTab />
                </div>
                <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
                  {d.customerName || d.dealName}
                </div>
                <div style={{ fontSize: '14px', color: theme.colors.grey500 }}>
                  {d.items?.length || 0} item{(d.items?.length || 0) !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                {d.isListingService && <Badge color="purple">Consignment</Badge>}
                {d.postcode && <Badge>{d.postcode}</Badge>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  // Testing Screen
  if (step === 'testing' && deal && currentItem) {
    return (
      <QuoteNotesLayout dealId={deal.id} mainWidth={700} aside={<CustomerDealsPanel dealId={deal.id} screen="testing" onAct={openFromPanel} />} style={{ margin: bp === 'phone' ? '24px 0' : '40px 0', padding: bp === 'phone' ? '0 12px' : '0 24px' }}>
        {showUntestedModal && (
          <UntestedConfirmModal
            itemName={currentItem?.name || currentItem?.productName}
            onConfirm={confirmUntested}
            onCancel={() => setShowUntestedModal(false)}
          />
        )}
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
              <DealLink {...dealLinkProps(deal)} newTab />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
              Product Testing
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => setConfirmingCancel(true)}>Cancel</Button>
            <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
          </div>
        </div>

        {confirmingCancel && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Discard this item's test?</div>
            <div style={{ fontSize: '14px', color: theme.colors.grey500, marginBottom: '12px' }}>
              Other items on this deal keep their results.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="danger" size="sm" disabled={saving} onClick={discardCurrentItem}>Discard</Button>
              <Button variant="secondary" size="sm" disabled={saving} onClick={() => setConfirmingCancel(false)}>Keep</Button>
            </div>
          </Card>
        )}

        {/* Customer note - per deal, so above the item tabs */}
        <CustomerNotePanel notes={submission.submission?.notes} style={{ marginBottom: '20px' }} />

        {/* Item Tabs - for multi-item deals */}
        {deal.items.length > 1 && (
          <ItemTabs
            items={deal.items}
            currentIndex={currentItemIndex}
            completedIndices={completedItemIndices}
            inProgressIndices={inProgressItemIndices}
            onSelectItem={switchToItem}
          />
        )}

        {/* Item Info */}
        <Card style={{ marginBottom: '20px' }}>
          <div data-testid="testing-item-header">
            <div style={{ fontWeight: 600, fontSize: '18px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {currentItem.name || currentItem.productName || `Item ${currentItemIndex + 1}`}
              {/* One line, several items: the tester tests every unit (spec 20261008). */}
              {itemQuantity(currentItem) > 1 && (
                <>
                  <QuantityBadge quantity={currentItem.quantity} />
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#B8860B' }}>
                    Test each of the {itemQuantity(currentItem)} units
                  </span>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '16px', color: theme.colors.grey500, fontSize: '14px', flexWrap: 'wrap' }}>
              {deal.isListingService && <Badge color="purple">Consignment</Badge>}
              {/* Consignment: there IS no buy — we sell for the customer
                  (spec §6: BP never surfaces on the consignment path). */}
              {!deal.isListingService && currentItem.buyPrice && <span>Buy: {formatCurrency(currentItem.buyPrice)}</span>}
              {currentItem.sellPrice && <span>{deal.isListingService ? 'Lists at' : 'Sell'}: {formatCurrency(currentItem.sellPrice)}</span>}
              {currentItem.productType && (
                <Badge color={needsCategoryReview ? 'red' : 'blue'}>
                  {currentItem.productType}
                  {productTypes?.groups && ` (${getMainCategoriesFromGroups(currentItem.productType, productTypes.groups).join(', ') || '?'})`}
                </Badge>
              )}
            </div>
          </div>
        </Card>

        {/* Product Type - Required */}
        {showProductTypeSelector ? (
          <ProductTypeSelector
            productTypes={productTypes}
            onSelect={handleProductTypeSelect}
            onCancel={() => setShowProductTypeSelector(false)}
            loading={productTypesLoading}
            currentSelection={currentItem.productType}
          />
        ) : !currentItem.productType ? (
          <Alert type="error">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Product type not found in database. Select a type to continue testing.</span>
              <Button variant="secondary" size="sm" onClick={openProductTypeSelector}>Select Type</Button>
            </div>
          </Alert>
        ) : needsCategoryReview ? (
          <Alert type="error">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '12px' }}>
              <span>
                <strong>Category needs review.</strong>
                {' '}{suggestedCategory ? <code>"{suggestedCategory}"</code> : 'a value'} — not a canonical category. Pick the closest match before continuing.
              </span>
              <Button variant="secondary" size="sm" onClick={openProductTypeSelector}>Pick Category</Button>
            </div>
          </Alert>
        ) : (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Product Type</div>
                <div style={{ fontWeight: 600, marginTop: '4px' }}>
                  {currentItem.productType}
                  {productTypes?.groups && (
                    <span style={{ fontWeight: 400, color: theme.colors.grey500 }}>
                      {' '}({getMainCategoriesFromGroups(currentItem.productType, productTypes.groups).join(', ') || '?'})
                    </span>
                  )}
                </div>
                {checklist?.bucket && (
                  <div style={{ fontSize: '12px', color: theme.colors.grey400, marginTop: '2px' }}>
                    Checklist: {checklist.bucket}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={openProductTypeSelector}>Change</Button>
            </div>
          </Card>
        )}

        {/* Customer Stated Condition */}
        {currentItem.customerCondition && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px' }}>
              Customer stated condition: <strong>{displayCondition(currentItem.customerCondition)}</strong>
            </div>
          </Card>
        )}

        {/* Only show rest of form if product type is set AND canonical */}
        {isCategoryReadyForTesting(currentItem) && (
          <>
            {/* 0. Sell as: one listing, or split into parts (Trac #1111) */}
            {splitAllowed && (
              <SellAsPanel
                split={testResult.split}
                lineBuy={lineBuy}
                onOneListing={stopSplitting}
                onSplit={startSplitting}
                onAddPart={addSplitPart}
                onRemovePart={removeSplitPart}
                onUpdatePart={changeSplitPart}
              />
            )}
            {testResult.split && (
              <PartTabs parts={testResult.split.parts} activeIndex={activePartIndex} onSelect={selectPart} />
            )}

            {/* 1. Overall Condition. A split item takes each part's own condition. */}
            {!testResult.split && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, marginBottom: '12px' }}>Overall Condition *</div>
                <ConditionPicker
                  options={TESTING_CONDITION_OPTIONS}
                  value={testResult.overallCondition}
                  onChange={(val) => setTestResult(prev => ({ ...prev, overallCondition: val }))}
                />
              </Card>
            )}

            {/* 2. Broken Item - Expected */}
            {isBroken && customerExpectedBroken && (
              <>
                <Alert type="info">
                  Item was stated as "Not Working" by customer. Continue with broken item documentation.
                </Alert>
                <Card style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>
                    Quoted as not working: <strong>{formatCurrency(currentItem.buyPrice || 0)}</strong>
                  </div>
                  <label style={{ fontSize: '13px', color: theme.colors.grey500, display: 'block', marginBottom: '8px' }}>
                    Spares/Repair Offer Amount *
                  </label>
                  <input
                    type="number"
                    value={testResult.sparesAmount}
                    onChange={(e) => setTestResult(prev => ({ ...prev, sparesAmount: e.target.value }))}
                    placeholder="Enter amount to offer"
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px', outline: 'none'
                    }}
                  />
                </Card>
              </>
            )}

            {/* 2. Broken Item - Unexpected */}
            {isUnexpectedlyBroken && (
              <Card style={{ marginBottom: '20px', border: `2px solid ${theme.colors.crimson}` }}>
                <div style={{ fontWeight: 600, marginBottom: '16px', color: theme.colors.crimson }}>
                  Spares or Repair - Customer Expected Working
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                    Original Buy Price: <strong>{formatCurrency(currentItem.buyPrice || 0)}</strong>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', color: theme.colors.grey500, display: 'block', marginBottom: '8px' }}>
                    Spares/Repair Offer Amount *
                  </label>
                  <input
                    type="number"
                    value={testResult.sparesAmount}
                    onChange={(e) => setTestResult(prev => ({ ...prev, sparesAmount: e.target.value }))}
                    placeholder="Enter amount to offer"
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '13px', color: theme.colors.grey500, display: 'block', marginBottom: '8px' }}>
                    Describe the issue *
                  </label>
                  <textarea
                    value={testResult.brokenDetails}
                    onChange={(e) => setTestResult(prev => ({ ...prev, brokenDetails: e.target.value }))}
                    placeholder="Describe what's wrong with the item..."
                    rows={3}
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px',
                      resize: 'vertical', outline: 'none'
                    }}
                  />
                </div>
              </Card>
            )}

            {/* 3. Physical Condition - Only if NOT unexpectedly broken */}
            {hasCondition && !isUnexpectedlyBroken && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, marginBottom: '16px' }}>Physical Condition Assessment *</div>
                {PHYSICAL_FIELDS.map(field => (
                  <div key={field.key} style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>{field.label}</div>
                    <OptionGroup
                      options={PHYSICAL_SCORE_OPTIONS}
                      value={view.physicalCondition[field.key]}
                      onChange={(val) => updatePhysicalCondition(field.key, val)}
                    />
                  </div>
                ))}
              </Card>
            )}

            {/* 4. Testing Level - Only if NOT unexpectedly broken */}
            {hasCondition && !isUnexpectedlyBroken && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, marginBottom: '12px' }}>
                  Testing Level *
                  <span style={{ fontWeight: 400, color: theme.colors.grey500, fontSize: '13px', marginLeft: '8px' }}>
                    ({isElectrical ? 'Electrical' : 'Non-Electrical'} Item)
                  </span>
                </div>
                <OptionGroup
                  options={testingLevelOptions}
                  value={view.testingLevel}
                  onChange={(val) => updateView(v => ({ ...v, testingLevel: val }))}
                  columns={testingLevelOptions.length}
                />
              </Card>
            )}

            {/* 5. Checklist - Only if testing level is not Untested */}
            {view.testingLevel && view.testingLevel !== 'Untested' && checklist?.questions?.length > 0 && (
              <Card style={{ marginBottom: '20px' }} padding={false}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.colors.grey200}`, fontWeight: 600 }}>
                  Test Checklist - {checklist.bucket}
                </div>
                <div style={{ padding: '12px' }}>
                  {checklist.questions.map((q) => {
                    const questionId = q.id || q.key
                    const questionLabel = q.label || (typeof q === 'string' ? q : questionId)
                    const result = view.checklistResults[questionId]
                    return (
                      <div
                        key={questionId}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '12px 14px', marginBottom: '8px',
                          background: result === 'OK' ? theme.colors.greenLight :
                            result === 'FAIL' ? theme.colors.redLight :
                            result === 'N/A' ? theme.colors.grey100 : theme.colors.white,
                          border: `1px solid ${theme.colors.grey200}`,
                          borderRadius: theme.radius.md
                        }}
                      >
                        <span style={{ fontWeight: 500, flex: 1 }}>{questionLabel}</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {CHECKLIST_RESULTS.map(r => (
                            <button
                              key={r}
                              onClick={() => updateChecklistResult(questionId, r)}
                              style={{
                                padding: '6px 12px', borderRadius: theme.radius.sm,
                                background: result === r
                                  ? (r === 'OK' ? theme.colors.greenOnions : r === 'FAIL' ? theme.colors.crimson : theme.colors.grey400)
                                  : theme.colors.white,
                                color: result === r ? theme.colors.white : theme.colors.charcoal,
                                fontWeight: 600, fontSize: '12px', cursor: 'pointer',
                                border: `1px solid ${theme.colors.grey200}`
                              }}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Card>
            )}

            {/* 6. Included items - required once a condition is chosen (W7), broken items included */}
            {needsIncludedItems && (
              <Card style={{ marginBottom: '20px' }}>
                <IncludedItemsPicker
                  theme={theme}
                  options={includedItemsOptions}
                  selected={view.includedItems}
                  otherText={view.otherAccessories}
                  onToggle={toggleIncludedItem}
                  onOtherChange={(text) => updateView(v => ({ ...v, otherAccessories: text }))}
                />
              </Card>
            )}

            {/* 7. Needs Repair */}
            {!isUnexpectedlyBroken && testResult.overallCondition && testResult.overallCondition !== 'Broken' && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: testResult.needsRepair ? '16px' : 0 }}>
                  <input
                    type="checkbox"
                    checked={testResult.needsRepair}
                    onChange={(e) => setTestResult(prev => ({ ...prev, needsRepair: e.target.checked }))}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setTestResult(prev => ({ ...prev, needsRepair: !prev.needsRepair }))}>
                    Mark for Repair List
                  </label>
                </div>
                {testResult.needsRepair && (
                  <textarea
                    value={testResult.repairIssues}
                    onChange={(e) => setTestResult(prev => ({ ...prev, repairIssues: e.target.value }))}
                    placeholder="Describe repair issues..."
                    rows={3}
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px',
                      resize: 'vertical', outline: 'none'
                    }}
                  />
                )}
              </Card>
            )}

            {/* 8. Notes */}
            <Card style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>Additional Notes</div>
              <ClickyTextNotes
                value={view.notes}
                onChange={notes => updateView(v => ({ ...v, notes }))}
                productType={activePart?.subCategory || currentItem.productType}
                theme={theme}
              />
            </Card>

            {error && <Alert type="error">{error}</Alert>}

            {(() => {
              // Footer state machine (Trac #850 Phase 1):
              //   - bundle submit in flight  -> "Submitting deal…"
              //   - current item already marked complete  -> green confirmation
              //   - canSubmit && not yet complete  -> Done button to flip status to 'complete'
              //   - canSubmit false  -> grey hint listing what's missing
              const currentStatus = allItemResults.find(r => r.itemIndex === currentItemIndex)?.status
              const isCurrentComplete = currentStatus === 'complete'
              const isBundleSubmitting = saving && testableCount > 0 &&
                allItemResults.filter(r => r.status === 'complete' && testableIndices.includes(r.itemIndex)).length === testableCount

              if (isBundleSubmitting) {
                return (
                  <div style={{
                    padding: '12px 16px',
                    background: theme.colors.blueLight,
                    borderRadius: theme.radius.md,
                    fontSize: '13px',
                    color: theme.colors.imBlue,
                    textAlign: 'center',
                    fontWeight: 500,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}>
                    <Spinner size={14} /> Submitting deal…
                  </div>
                )
              }

              if (isCurrentComplete) {
                return (
                  <div style={{
                    padding: '12px 16px',
                    background: theme.colors.greenLight,
                    borderRadius: theme.radius.md,
                    fontSize: '14px',
                    color: theme.colors.greenOnions,
                    textAlign: 'center',
                    fontWeight: 600
                  }}>
                    ✓ Item marked complete. Edit any field to revise — you'll re-confirm with the Done button.
                  </div>
                )
              }

              if (canSubmit()) {
                return (
                  <Button
                    variant={isBroken ? 'danger' : 'success'}
                    disabled={saving}
                    onClick={markCurrentItemComplete}
                    fullWidth
                    size="lg"
                  >
                    {saving ? <><Spinner size={18} light /> Saving…</> : (
                      testResult.split ? splitDoneLabel(testResult.split) :
                      isBroken ? 'Done — Mark Spares or Repair' :
                      testResult.needsRepair ? 'Done — Mark for Repair' :
                      'Done — Mark Item Complete'
                    )}
                  </Button>
                )
              }

              return (
                <div style={{
                  padding: '12px 16px',
                  background: theme.colors.grey100,
                  borderRadius: theme.radius.md,
                  fontSize: '13px',
                  color: theme.colors.grey500,
                  textAlign: 'center',
                  fontWeight: 500
                }}>
                  {testResult.split && splitIssues.map(issue => <div key={issue}>{issue}</div>)}
                  {!testResult.split && !testResult.overallCondition && 'Select overall condition • '}
                  {!currentItem.productType && 'Set product type • '}
                  {isBroken && !testResult.sparesAmount && 'Enter spares amount • '}
                  {isUnexpectedlyBroken && !testResult.brokenDetails && 'Describe the issue • '}
                  {!testResult.split && !isBroken && !isUnexpectedlyBroken && !Object.values(testResult.physicalCondition).every(v => v) && 'Complete physical assessment • '}
                  {!testResult.split && !isBroken && !isUnexpectedlyBroken && !testResult.testingLevel && 'Select testing level • '}
                  {!testResult.split && needsIncludedItems && !includedItemsAnswered(testResult.includedItems, testResult.otherAccessories) && 'Tick the included items'}
                </div>
              )
            })()}
          </>
        )}
      </QuoteNotesLayout>
    )
  }

  // Success Screen
  if (step === 'success') {
    const brokenItemsCount = submitResult?.brokenItems || 0
    const brokenItemDetails = submitResult?.brokenItemDetails || []
    const repairItems = allItemResults.filter(r => r.needsRepair)
    const passedItems = allItemResults.filter(r => r.overallCondition !== 'Broken' && !r.needsRepair)
    // Items, not lines (spec 20261008): "10 items (4 lines)".
    const testedPhrase = itemCountPhrase(deal?.items || [])
    const isListingService = deal?.isListingService || false

    const autoQueuedItems = submitResult?.autoQueuedItems || []
    const autoQueuedSuccessCount = autoQueuedItems.filter(d => d.success).length

    return (
      <div style={{ maxWidth: '560px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>Testing Complete</h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '8px' }}>
            All {testedPhrase} tested{autoQueuedSuccessCount > 0 ? ` and queued for photography` : ''}.
            {submitResult?.splitPerformed ? (
              ` Deal split: ${submitResult.passedItemCount} passed → Awaiting Payment, ${submitResult.brokenItemCount} broken → New Issue Trade`
            ) : submitResult?.targetStage && (
              ` Deal moved to ${
                submitResult.targetStage === 'contractsent' ? 'Tested/Awaiting Payment' :
                submitResult.targetStage === '588059587' ? 'Issue with Item' :
                submitResult.targetStage
              } stage.`
            )}
          </p>

          {/* Customer email blocked — the operator must act manually (spec_20260924 D7).
              Same amber warning box style as the consignment-issues notice below. */}
          {submitResult?.customerEmailBlocked && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⚠️ Customer NOT Emailed
              </div>
              <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                {submitResult.customerEmailBlocked.reason}.{' '}
                {submitResult.customerEmailBlocked.kind === 'prism_unavailable'
                  ? 'Re-save in a few minutes, or email the customer manually.'
                  : 'Email the customer manually. Re-saving will not fix this.'}
              </div>
            </div>
          )}

          {/* Deal Info */}
          <div style={{
            background: theme.colors.grey100,
            borderRadius: theme.radius.md,
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Deal ID</div>
            <div style={{ fontFamily: theme.fonts.sans }}><DealLink {...dealLinkProps(deal)} newTab /></div>
            <div style={{ color: theme.colors.grey500, marginTop: '4px' }}>{testedPhrase} tested</div>
          </div>

          {/* Consignment - Issues Found */}
          {deal?.isListingService && submitResult?.listingServiceIssue && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⚠️ Consignment Item Has Issues
              </div>
              <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                Manual intervention required. Customer will be notified.
              </div>
            </div>
          )}

          {/* Broken Items */}
          {brokenItemDetails.length > 0 && (
            <div style={{
              background: theme.colors.redLight,
              border: `1px solid ${theme.colors.crimson}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⚠️ {brokenItemDetails.length} Broken Item{brokenItemDetails.length !== 1 ? 's' : ''}
              </div>
              {brokenItemDetails.map((item, i) => (
                <div key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>
                  • {item.itemName}: {item.issue}
                </div>
              ))}
            </div>
          )}

          {/* Items Marked for Repair */}
          {repairItems.length > 0 && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                🔧 {repairItems.length} Item{repairItems.length !== 1 ? 's' : ''} Marked for Repair
              </div>
              {repairItems.map((item, i) => (
                <div key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>
                  • {item.itemName}: {item.repairIssues}
                </div>
              ))}
            </div>
          )}

          {/* Split Deal: Some items passed, some broken - split into two deals */}
          {submitResult?.splitPerformed && (
            <div style={{
              background: theme.colors.grey100,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              fontSize: '14px',
              textAlign: 'left'
            }}>
              {/* Passed items - green */}
              <div style={{
                background: theme.colors.greenLight,
                border: `1px solid ${theme.colors.greenOnions}`,
                borderRadius: theme.radius.md,
                padding: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.greenOnions }}>
                  <span>✓</span> {submitResult.passedItemCount} Item{submitResult.passedItemCount !== 1 ? 's' : ''} Passed - Ready to List
                </div>
                {submitResult.passedItems?.map((item, i) => (
                  <div key={i} style={{ fontSize: '13px', marginLeft: '20px', color: theme.colors.grey600 }}>
                    • {item.name}{itemQuantity(item) > 1 ? ` ×${itemQuantity(item)}` : ''} - £{parseFloat(item.buyPrice || 0).toFixed(2)}
                  </div>
                ))}
              </div>

              {/* Broken items - amber/red */}
              <div style={{
                background: theme.colors.amberLight,
                border: `1px solid ${theme.colors.sunburst}`,
                borderRadius: theme.radius.md,
                padding: '12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.sunburst }}>
                  <span>⚠️</span> {submitResult.brokenItemCount} Item{submitResult.brokenItemCount !== 1 ? 's' : ''} Not Listed - Moved to New Issue Trade
                </div>
                <div style={{ fontSize: '12px', color: theme.colors.grey500, marginBottom: '8px', marginLeft: '28px' }}>
                  New Deal ID: <strong style={{ fontFamily: 'monospace', color: theme.colors.sunburst }}>{submitResult.splitDealId}</strong>
                </div>
                {submitResult.brokenItemsForSplit?.map((item, i) => (
                  <div key={i} style={{ fontSize: '13px', marginLeft: '20px', color: theme.colors.grey600 }}>
                    • {item.name}{itemQuantity(item) > 1 ? ` ×${itemQuantity(item)}` : ''} - Spares: £{parseFloat(item.sparesAmount || 0).toFixed(2)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auto-queued for Photography (Trac #867). All passed items queue automatically
              at save-results time; the user no longer has to click anything. Consignment
              items queue the same way — their draft is created at photography completion,
              not at test time (spec D5). */}
          {autoQueuedItems.length > 0 && (
            <div style={{
              background: theme.colors.greenLight,
              border: `1px solid ${theme.colors.greenOnions}`,
              borderRadius: theme.radius.md,
              padding: '20px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>📷</span>
                {autoQueuedSuccessCount} item{autoQueuedSuccessCount !== 1 ? 's' : ''} queued for photography
              </div>
              {autoQueuedItems.map((q, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 12px', background: theme.colors.white,
                  borderRadius: theme.radius.sm, marginBottom: '6px'
                }}>
                  <span style={{ fontSize: '16px' }}>{q.success ? '✅' : '❌'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{q.itemName}</div>
                    {q.success && q.photographyQueueId && (
                      <div style={{ fontSize: '12px', color: theme.colors.grey500 }}>Queue ID: {q.photographyQueueId}</div>
                    )}
                    {!q.success && q.error && (
                      <div style={{ fontSize: '12px', color: theme.colors.crimson }}>{q.error}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info: Deal going to Issue Trades because of broken items (non-split case) */}
          {!isListingService && brokenItemDetails.length > 0 && !submitResult?.splitPerformed && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              fontSize: '14px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⚠️</span> Items Require Customer Response
              </div>
              <p style={{ margin: '0 0 8px 0', color: theme.colors.grey600 }}>
                This deal has {brokenItemDetails.length} broken item{brokenItemDetails.length !== 1 ? 's' : ''}. It will be moved to <strong>Issue with Item</strong> stage.
              </p>
              <p style={{ margin: 0, color: theme.colors.grey500, fontSize: '13px' }}>
                Items will be queued for photography after the customer responds in Issue Trades.
              </p>
            </div>
          )}

          <Button variant="ghost" onClick={reset} fullWidth>Done</Button>
        </Card>
      </div>
    )
  }

  return null
}

export default Testing
